import { useFormContext } from 'react-hook-form';
import { celularLabel, cnpjLabel, cpfLabel } from '@/Utils/FnUtils';
import {
  GENDER_TEXT,
  TRANSLATE_ACCOUNT_TYPE,
} from '@/_features/motoboys/constants';
import { ENUM_TITLES_SECTION } from '../../constants';
import type { PreRegisterInfoProps } from '../../types';
import {
  formatApprovalDate,
  isValidDateValue,
} from '../../utils/format-approval-date';
import { SectionInfo } from '../section-info';
import type { FormType } from '../approval-mat-form/types';

function ApprovalMatSections({
  deliverymanInfos,
}: {
  deliverymanInfos: PreRegisterInfoProps;
}) {
  const { getValues } = useFormContext<FormType>();

  const {
    showPersonalData,
    showBankData,
    showDeliverymanPicture,
    showDeliverymanWithRgPicture,
    showRg,
    showCnh,
    showCrlv,
  } = getValues();

  const rgData = deliverymanInfos?.preRegistration?.rg;
  const bankData = deliverymanInfos?.contaBancaria;
  const cnhData = deliverymanInfos?.preRegistration?.cnh;
  const crlvData = deliverymanInfos?.preRegistration?.crlv;
  const deliverymanPictureData =
    deliverymanInfos?.preRegistration?.deliverymanPictureId;
  const deliverymanWithPicture =
    deliverymanInfos?.preRegistration?.deliverymanWithRgPictureId;

  const detailsData = [
    {
      key: 'Nome',
      value: deliverymanInfos?.nome || '',
    },
    {
      key: 'Data de nascimento',
      value: deliverymanInfos?.dataNascimento
        ? formatApprovalDate(deliverymanInfos.dataNascimento)
        : '',
    },
    {
      key: 'Gênero',
      value: deliverymanInfos?.gender
        ? GENDER_TEXT[deliverymanInfos?.gender]
        : '',
    },
    {
      key: 'E-mail',
      value: deliverymanInfos?.email || '',
    },
    {
      key: 'Telefone',
      value: deliverymanInfos?.celular
        ? celularLabel(deliverymanInfos?.celular)
        : '',
    },
    {
      key: 'Cpf',
      value: deliverymanInfos?.cpf ? cpfLabel(deliverymanInfos?.cpf) : '',
    },
    {
      key: 'Tipo do veículo',
      value: deliverymanInfos?.tipoVeiculo?.toLowerCase() || '',
    },
    {
      key: 'Endereço',
      value:
        `${deliverymanInfos?.enderecoResidencia?.logradouro} - ${deliverymanInfos?.enderecoResidencia?.numero} ` ||
        '',
    },
    {
      key: 'Complemento',
      value: deliverymanInfos?.enderecoResidencia?.complemento || '',
    },
    {
      key: 'Cidade',
      value: deliverymanInfos?.cityName || '',
    },
    {
      key: 'Estado',
      value: deliverymanInfos?.cityUf || '',
    },
  ];

  const detailsBankData = [
    {
      key: 'Nome do titular',
      value: bankData?.nomeDestinatario || '',
    },
    {
      key: 'Cpf do titular',
      value:
        bankData?.cpfCnpjDestinatario?.length === 11
          ? cpfLabel(bankData?.cpfCnpjDestinatario || '')
          : cnpjLabel(bankData?.cpfCnpjDestinatario || ''),
    },
    {
      key: 'Banco',
      value: bankData?.bancoNome || '',
    },
    {
      key: 'Tipo de conta',
      value: bankData?.tipoConta
        ? TRANSLATE_ACCOUNT_TYPE[bankData.tipoConta]
        : '',
    },
    {
      key: 'Agência',
      value:
        `${bankData?.bancoAgencia}${
          bankData?.bancoAgenciaDigito ? `-${bankData.bancoAgenciaDigito}` : ''
        }` || '',
    },
    {
      key: 'Conta',
      value: `${bankData?.numeroConta} - ${bankData?.digitoConta} ` || '',
    },
  ];

  const deatailsCNH = [
    {
      key: 'Número CNH',
      value: cnhData?.identification || '',
    },
    {
      key: 'Categoria',
      value: cnhData?.category || '',
    },
    {
      key: 'Data de emissão',
      value: formatApprovalDate(cnhData?.emissionDate),
    },
    {
      key: 'Data de validade',
      value: formatApprovalDate(cnhData?.expirationDate),
    },
  ];

  const deatailsCRLV = [
    {
      key: 'Categoria do veículo',
      value: crlvData?.vehicleCategory || '',
    },
    {
      key: 'Nome do proprietário',
      value: crlvData?.ownerName || '',
    },
    {
      key: 'Placa do veículo',
      value: crlvData?.licensePlate || '',
    },
    {
      key: 'Código Renavam',
      value: crlvData?.renavamCode || '',
    },
    {
      key: 'Modelo do veículo',
      value: crlvData?.vehicleModel || '',
    },
  ];

  return (
    <>
      {showPersonalData ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.PERSONAL_DATA}:`}
          details={detailsData}
          valueApprove="hasApprovedPersonalData"
          valueComment="commentsPersonalData"
        />
      ) : null}

      {showBankData && bankData ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.BANK_DATA}:`}
          details={detailsBankData}
          imageUrl={bankData?.firstBankReceiptPictureKey || ''}
          secondImageUrl={bankData?.secondBankReceiptPictureKey || ''}
          customFirstImageLabel="Primeira imagem"
          customSecondImageLabel="Segunda imagem"
          valueApprove="hasApprovedBankAccount"
          valueComment="commentsBankAccount"
        />
      ) : null}

      {showRg && rgData ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.ID_DOCUMENT}:`}
          imageUrl={rgData.imageFrontId}
          secondImageUrl={rgData.imageBackId}
          customFirstImageLabel="Frente"
          customSecondImageLabel="Verso"
          valueApprove="hasApprovedRg"
          valueComment="commentsRg"
        />
      ) : null}

      {showCnh && cnhData ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.DRIVER_LICENSE}:`}
          imageUrl={cnhData.imageId}
          details={deatailsCNH}
          valueApprove="hasApprovedCnh"
          valueComment="commentsCnh"
          disableApprove={
            !isValidDateValue(cnhData.emissionDate) ||
            !isValidDateValue(cnhData.expirationDate)
          }
        />
      ) : null}

      {showCrlv && crlvData ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.VEHICLE_REGISTRATION}:`}
          imageUrl={crlvData.imageId}
          details={deatailsCRLV}
          valueApprove="hasApprovedCrlv"
          valueComment="commentsCrlv"
        />
      ) : null}

      {showDeliverymanPicture ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.SELFIE}:`}
          imageUrl={deliverymanPictureData}
          valueApprove="hasApprovedDeliverymanPicture"
          valueComment="commentsDeliverymanPicture"
        />
      ) : null}

      {showDeliverymanWithRgPicture ? (
        <SectionInfo
          title={`${ENUM_TITLES_SECTION.SELFIE_WHIT_DOCUMENT}:`}
          imageUrl={deliverymanWithPicture}
          valueApprove="hasApprovedSelfieWithRg"
          valueComment="commentsSelfieWithRg"
        />
      ) : null}
    </>
  );
}

export { ApprovalMatSections };
